import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ConnectedApp, ProductInfo } from '@authsemply/shared';
import { Alert } from '../components/Alert';
import { AuthCard } from '../components/AuthCard';
import { VersionApp } from '../components/VersionApp';
import { CompteBetaAvis, TITRE_VERROU_BETA } from '../components/CompteBetaAvis';
import { MfaSection } from '../components/MfaSection';
import { MoyensConnexionSection } from '../components/MoyensConnexionSection';
import { SessionsSection } from '../components/SessionsSection';
import { api } from '../lib/api';
import { loadProducts } from '../lib/products';
import { useAuthStore } from '../store/auth.store';

export function AccountPage() {
  const navigate = useNavigate();
  const { user, loading, fetchMe, logout } = useAuthStore();
  const [apps, setApps] = useState<ConnectedApp[]>([]);
  const [products, setProducts] = useState<ProductInfo[]>([]);

  const loadApps = useCallback(() => {
    api
      .get<ConnectedApp[]>('/oidc/consents')
      .then(setApps)
      .catch(() => setApps([]));
  }, []);

  useEffect(() => {
    void fetchMe();
    void loadProducts().then(setProducts);
  }, [fetchMe]);

  // Lien `/compte#double-authentification` (depuis les paramètres des produits) :
  // la section n'existe qu'une fois le compte chargé, le navigateur ne peut donc
  // pas faire défiler seul jusqu'à elle.
  useEffect(() => {
    if (loading || !user) return;
    const ancre = window.location.hash.replace(/^#/, '');
    if (ancre) document.getElementById(ancre)?.scrollIntoView?.({ block: 'start' });
  }, [loading, user]);

  useEffect(() => {
    if (!loading && !user) navigate('/login');
    // Rattrape les chemins d'entrée qui ne passent pas par l'écran de
    // connexion — retour Google, session déjà ouverte, URL saisie à la main.
    else if (user?.mfaRequired) navigate('/double-authentification');
    else if (user) loadApps();
  }, [loading, user, navigate, loadApps]);

  async function revoke(clientId: string) {
    await api.delete(`/oidc/consent/${encodeURIComponent(clientId)}`);
    loadApps();
  }

  if (loading || !user) {
    return <AuthCard title="Chargement…">{null}</AuthCard>;
  }

  return (
    <AuthCard title="Mon compte" subtitle={user.email}>
      {/* Reconnexion pendant le délai de 30 jours (02/10/2026) : la suppression
          demandée est annulée — la personne doit le voir, pas seulement le lire
          dans sa boîte mail. */}
      <Alert
        variant="success"
        message={
          user.suppressionAnnuleeLe
            ? 'Vous vous êtes reconnecté : la suppression de votre compte, que vous aviez demandée, est annulée. Votre compte et votre espace restent tels quels.'
            : null
        }
      />
      {user.betaCompte ? <CompteBetaAvis /> : null}
      <dl style={{ fontSize: '0.9rem', lineHeight: 1.7, margin: '0 0 1.5rem' }}>
        <div>
          <strong>Nom :</strong> {user.displayName ?? '—'}
        </div>
        <div>
          <strong>E-mail vérifié :</strong> {user.emailVerified ? 'oui' : 'non'}
        </div>
      </dl>

      {/* Changer son mot de passe, connecté (04/10/2026). Les outils SEmply
          renvoient sur la même page depuis leurs paramètres de compte. */}
      <Link className="product-card" to="/compte/mot-de-passe" style={{ marginBottom: '1.5rem' }}>
        <strong>Mot de passe</strong>
        <span>Changer mon mot de passe</span>
      </Link>

      {/* L'état de la double authentification vient du serveur : après un
          changement, on relit le compte plutôt que de deviner localement. */}
      <MfaSection
        enabled={Boolean(user.mfaEnabled)}
        onChanged={() => void fetchMe()}
        verrouille={Boolean(user.betaCompte)}
      />

      {/* Comptes Google, Microsoft, Apple rattachés, et leur retrait (27/09/2026). */}
      <MoyensConnexionSection verrouille={Boolean(user.betaCompte)} />

      {/* Appareils connectés et fermeture à distance (recette AUTH, 05/10/2026). */}
      <SessionsSection verrouille={Boolean(user.betaCompte)} />

      <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Vos outils SEmply</h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem' }}>
        {products.map((product) => (
          <li key={product.clientId} style={{ marginBottom: '0.6rem' }}>
            <a className="product-card" href={product.url}>
              <strong>{product.name}</strong>
              {product.description ? <span>{product.description}</span> : null}
            </a>
          </li>
        ))}
      </ul>

      {/* Applications TIERCES (OAuth externes) uniquement : les outils SEmply
          sont first-party et n'apparaissent jamais ici. Section masquée tant
          qu'elle est vide — un « aucune application » permanent ne fait que
          semer le doute. */}
      {apps.length > 0 ? (
        <>
          <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Applications connectées</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', fontSize: '0.9rem' }}>
          {apps.map((app) => (
            <li
              key={app.client.clientId}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                padding: '0.6rem 0',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <span>
                <strong>{app.client.name}</strong>
                <div style={{ color: 'var(--text-color-light)', fontSize: '0.8rem' }}>
                  {app.scopes.join(', ')} — depuis le{' '}
                  {new Date(app.grantedAt).toLocaleDateString('fr-FR')}
                </div>
              </span>
              <button
                className="btn btn-secondary"
                style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                type="button"
                onClick={() => void revoke(app.client.clientId)}
              >
                Retirer
              </button>
            </li>
          ))}
          </ul>
        </>
      ) : null}

      {user?.administration ? (
        <a className="product-card" href="/admin" style={{ marginBottom: '1rem' }}>
          <strong>Back-office de la suite</strong>
          <span>Tickets, équipe, catalogue, produits et ouverture de la suite</span>
        </a>
      ) : null}

      {/* L'espace est l'autre moitié du compte : les personnes qui y ont accès
          et à qui il appartient. Un lien plutôt qu'une section ici — la gestion
          des membres est une page à part entière. */}
      <a
        className="product-card"
        href="/espace"
        style={{ marginBottom: '1.5rem' }}
      >
        <strong>Mon espace</strong>
        <span>Personnes ayant accès, rôles et propriétaire</span>
      </a>

      <button className="btn" type="button" onClick={() => void logout().then(() => navigate('/login'))}>
        Se déconnecter
      </button>

      {/* Suppression définitive, aussi possible depuis l'appli (01/10/2026). */}
      <div className="links">
        {user.betaCompte ? (
          <span title={TITRE_VERROU_BETA} aria-disabled="true" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
            Supprimer mon compte
          </span>
        ) : (
          <Link to="/compte/suppression">Supprimer mon compte</Link>
        )}
      </div>
      <VersionApp produit="SEmply" />
    </AuthCard>
  );
}
